/**
 * Button directional hover animation — desktop only
 *
 * For each .button that is NOT excluded, injects a <span class="mlrs-btn__layer">
 * and adds the .mlrs-btn class. On mouseenter/mouseleave, detects which edge the
 * pointer crossed and translates the layer in/out from that direction.
 *
 * CSS (button-hover-rhode.css) handles the layer's appearance; this file only
 * controls the transform + transition via inline styles.
 */
(function () {
  'use strict';

  /* ── Desktop guard ──────────────────────────────────────────────────── */
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  /* ── Animation duration from CSS variable set by theme.liquid ───────── */
  var rawDur   = getComputedStyle(document.documentElement)
                   .getPropertyValue('--mlrs-btn-duration').trim();
  var DURATION = parseInt(rawDur, 10) || 400;
  var EASING   = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';

  /* ── Exclusion list ──────────────────────────────────────────────────── */
  var EXCLUDED_CLASSES = [
    'header-menu__item',          /* desktop nav items */
    'header__icon',               /* header icon buttons */
    'header__burger-menu-button', /* burger */
    'swiper-button',              /* slider prev/next */
    'button-social',              /* social share icons */
    'button-link',                /* text-link styled buttons */
    'quantity__button',           /* +/- quantity controls */
    'gift-button',                /* cart gift icon */
    'mobile-menu-tab',            /* mobile category tabs */
    'js-header-mega-menu-toggler',/* mega menu togglers */
    'js-header-dropdown-menu-toggler',
    'js-accordion-control',       /* accordion buttons */
    'shopify-payment-button__button', /* Apple Pay / Google Pay */
    'shopify-payment-button__button--hidden',
  ];

  function isExcluded(el) {
    var cl = el.classList;
    for (var i = 0; i < EXCLUDED_CLASSES.length; i++) {
      if (cl.contains(EXCLUDED_CLASSES[i])) return true;
    }
    /* Ancestor checks */
    if (el.closest('[data-shopify="payment-button"]')) return true;
    if (el.closest('.header-menu'))                   return true; /* all nav items */
    if (el.closest('#headerDrawerMenu'))              return true; /* mobile menu */
    return false;
  }

  /* ── Direction detection ─────────────────────────────────────────────── */
  /* Returns the nearest edge ('top' | 'bottom' | 'left' | 'right')
     to the pointer position relative to the element bounding box. */
  function getEdge(e, el) {
    var r  = el.getBoundingClientRect();
    var x  = e.clientX - r.left;
    var y  = e.clientY - r.top;
    var t  = y / r.height;
    var b  = 1 - t;
    var l  = x / r.width;
    var ri = 1 - l;
    var m  = Math.min(t, b, l, ri);
    return m === t ? 'top' : m === b ? 'bottom' : m === l ? 'left' : 'right';
  }

  function edgeTransform(edge) {
    if (edge === 'top')    return 'translateY(-101%)';
    if (edge === 'bottom') return 'translateY(101%)';
    if (edge === 'left')   return 'translateX(-101%)';
    return 'translateX(101%)';
  }

  /* ── Initialise a single button ─────────────────────────────────────── */
  function initButton(btn) {
    if (btn._mlrsInit) return;
    btn._mlrsInit = true;

    btn.classList.add('mlrs-btn');

    /* Inject the hover layer as the last child of the button.
       It is visually behind the text (z-index:-1 in CSS).              */
    var layer = document.createElement('span');
    layer.className = 'mlrs-btn__layer';
    layer.setAttribute('aria-hidden', 'true');
    /* Start parked above, no transition — prevents flash on first hover */
    layer.style.transform  = 'translateY(-101%)';
    layer.style.transition = 'none';
    btn.appendChild(layer);

    var tVal = 'transform ' + DURATION + 'ms ' + EASING;

    btn.addEventListener('mouseenter', function (e) {
      var edge = getEdge(e, btn);

      /* 1. Snap to entry edge without animation */
      layer.style.transition = 'none';
      layer.style.transform  = edgeTransform(edge);

      /* 2. Force reflow — commits the snap before the next paint */
      void layer.offsetWidth;

      /* 3. Animate to fill */
      layer.style.transition = tVal;
      layer.style.transform  = 'translate(0, 0)';
    });

    btn.addEventListener('mouseleave', function (e) {
      var edge = getEdge(e, btn);
      /* Slide out toward the exit edge (transition already set from enter) */
      layer.style.transition = tVal;
      layer.style.transform  = edgeTransform(edge);
    });
  }

  /* ── Init all .button and .mlrs-cp__nav-pill elements present at load ── */
  function initAll() {
    document.querySelectorAll('.button, .mlrs-cp__nav-pill').forEach(function (btn) {
      if (!isExcluded(btn)) initButton(btn);
    });
    /* Featured-products custom CTA links — not .button elements */
    document.querySelectorAll('.featured-products .custom-add-to-cart-btn').forEach(function (btn) {
      initButton(btn);
    });
    /* Google Pay button — Rhode directional fill (bypasses payment-button exclusion) */
    document.querySelectorAll(
      'shopify-google-pay-button button, .shopify-google-pay-button button'
    ).forEach(function (btn) {
      if (!btn._mlrsInit) initButton(btn);
    });
  }

  /* ── Watch for dynamically added buttons (cart drawer, quick view …) ── */
  function observeDynamic() {
    var mo = new MutationObserver(function (records) {
      records.forEach(function (rec) {
        rec.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          var isPill  = node.classList && node.classList.contains('mlrs-cp__nav-pill');
          var isBtn   = node.classList && node.classList.contains('button');
          var isFpBtn = node.classList && node.classList.contains('custom-add-to-cart-btn')
                        && node.closest && node.closest('.featured-products');
          var btns;
          if (isBtn || isPill || isFpBtn) {
            btns = [node];
          } else {
            btns = Array.from(node.querySelectorAll('.button, .mlrs-cp__nav-pill'));
            Array.prototype.push.apply(
              btns,
              Array.from(node.querySelectorAll('.custom-add-to-cart-btn')).filter(function (b) {
                return b.closest && b.closest('.featured-products');
              })
            );
          }
          btns.forEach(function (btn) {
            if (!isExcluded(btn)) initButton(btn);
          });
          /* Google Pay button (dynamically injected by Shopify) */
          var gpayBtns = (node.tagName === 'BUTTON' && node.closest &&
              (node.closest('shopify-google-pay-button') || node.closest('.shopify-google-pay-button')))
            ? [node]
            : Array.from(node.querySelectorAll(
                'shopify-google-pay-button button, .shopify-google-pay-button button'
              ));
          gpayBtns.forEach(function (btn) { if (!btn._mlrsInit) initButton(btn); });
        });
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  /* ── iOS :active fix — makes CSS :active reliable on touch devices ──── */
  document.addEventListener('touchstart', function () {}, { passive: true });

  /* ── Boot ────────────────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
      observeDynamic();
    });
  } else {
    initAll();
    observeDynamic();
  }

})();
